import { Icon } from "@circle-react-shared/Icon";
import { Typography } from "@circle-react-shared/uikit/Typography";
import type { CheckoutVariant } from "@circle-react/components/Paywalls/Checkout/types/CheckoutVariant";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { PaywallCustomization } from "@circle-react/types/PaywallCustomization";

import { usePaywallCustomizationContext } from "../PaywallCustomizationProvider";
import type { UseCountdownOptions } from "./hooks/useCountdown";
import { useMaxWidthApplied } from "./hooks/useMaxWidthApplied";

interface CountdownComponentProps {
  className?: string;
  countdownProperties: PaywallCustomization["countdown_properties"];
  useHook: (props: UseCountdownOptions) => {
    isExpired: boolean;
    timeRemainingText: string;
  };
  variant: CheckoutVariant;
}

const textGreen = "text-green-700";
const textDefault = "text-[#191b1f]";

const VARIANT_STYLES = {
  main: {
    container: "bg-primary rounded-3xl border",
    icon: "",
    text: "",
  },
  offer: {
    container: "bg-green-50 rounded-t-2xl border-t border-x w-full",
    icon: textGreen,
    text: textGreen,
  },
  // Offer is using fixed colors on purpose
  offer_v2: {
    container:
      "bg-white rounded-xl border w-full sm:w-auto px-6 border-[#e4e7eb]",
    icon: textDefault,
    text: textDefault,
  },
} as const;

export const CountdownComponent = ({
  countdownProperties,
  useHook,
  variant = "main",
  className,
}: CountdownComponentProps) => {
  const { checkoutPreviewContext } = usePaywallCustomizationContext();
  const { isExpired, timeRemainingText } = useHook({ countdownProperties });
  const isHidden =
    countdownProperties.action.action_type === "do_nothing" && isExpired;
  const { contentRef, maxWidth } = useMaxWidthApplied(!isHidden);

  if (isHidden) return null;

  const shouldUseMaxWidth = !checkoutPreviewContext && maxWidth > 0;
  const styles = VARIANT_STYLES[variant];

  return (
    <div
      className={classNames(
        "border-primary flex flex-row items-center justify-center gap-x-2 py-2.5",
        className,
        styles.container,
      )}
      data-testid="countdown-container"
    >
      <Icon type="16-clock" viewBox="0 0 16 16" className={styles.icon} />
      <div className="flex flex-row items-center justify-center gap-x-1">
        <Typography.LabelSm weight="medium" color={styles.text}>
          {countdownProperties.displayed_text}
        </Typography.LabelSm>
        <div
          ref={contentRef}
          style={{ minWidth: shouldUseMaxWidth ? `${maxWidth}px` : "auto" }}
        >
          <Typography.LabelSm weight="semibold" color={styles.text}>
            {timeRemainingText}
          </Typography.LabelSm>
        </div>
      </div>
    </div>
  );
};
